/**
 * Refunds API client
 *
 * Stream 2 Phase 2-2b / Plan §10 Stream 2 / Design §5.2 A3 退款双人审批.
 *
 * 包装 axios apiClient 调用 admin-service /refunds 端点；返回类型与 admin-service
 * ServiceResponse 形状对齐。所有写动作通过 client.ts 自动注入 Idempotency-Key。
 */
import { apiClient } from './client';

export type RefundStatus =
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'executed'
  | 'failed';

export interface RefundListItem {
  id: string;
  paymentRecordId: string;
  orderId: string | null;
  workId: string | null;
  userId: string;
  refundAmount: number;          // 单位：分
  reason: string;
  isSubstitutePayment: boolean;
  status: RefundStatus;
  initiatorId: string;
  initiatedAt: string;
  approverId: string | null;
  approvedAt: string | null;
  executorId: string | null;
  executedAt: string | null;
  rejectReason: string | null;
  executionFailureReason: string | null;
  outRefundNo: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RefundDetail extends RefundListItem {
  initiatorNote?: string | null;
  approvalNote?: string | null;
  executionNote?: string | null;
}

export interface ListRefundsResponse {
  items: RefundListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListRefundsFilter {
  status?: RefundStatus;
  initiatorId?: string;
  approverId?: string;
  executorId?: string;
  isSubstitutePayment?: boolean;
  fromDate?: string; // ISO
  toDate?: string;   // ISO
  page?: number;
  pageSize?: number;
}

export interface InitiateRefundInput {
  paymentRecordId: string;
  refundAmount: number; // 单位：分
  reason: string;
  isSubstitutePayment: boolean;
  orderId?: string;
  workId?: string;
  initiatorNote?: string;
}

export interface ApproveRefundInput {
  approvalNote?: string;
}

export interface RejectRefundInput {
  rejectReason: string;
}

export interface ExecuteRefundInput {
  executionNote?: string;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/** GET /refunds — 退款列表查询（分页 + 多字段过滤）。 */
export async function listRefunds(
  filter: ListRefundsFilter = {},
): Promise<ListRefundsResponse> {
  const params: Record<string, string | number | boolean> = {};
  if (filter.status) params.status = filter.status;
  if (filter.initiatorId) params.initiatorId = filter.initiatorId;
  if (filter.approverId) params.approverId = filter.approverId;
  if (filter.executorId) params.executorId = filter.executorId;
  if (filter.isSubstitutePayment != null) params.isSubstitutePayment = filter.isSubstitutePayment;
  if (filter.fromDate) params.fromDate = filter.fromDate;
  if (filter.toDate) params.toDate = filter.toDate;
  if (filter.page != null) params.page = filter.page;
  if (filter.pageSize != null) params.pageSize = filter.pageSize;

  const resp = await apiClient.get<ServiceResponse<ListRefundsResponse>>('/refunds', {
    params,
  });
  return resp.data.data;
}

/** GET /refunds/:id — 退款详情（含 3 个 note 字段）。 */
export async function getRefund(id: string): Promise<RefundDetail> {
  const resp = await apiClient.get<ServiceResponse<RefundDetail>>(
    `/refunds/${encodeURIComponent(id)}`,
  );
  return resp.data.data;
}

/** POST /refunds — 发起退款（REFUND_INITIATE）。 */
export async function initiateRefund(input: InitiateRefundInput): Promise<void> {
  await apiClient.post<ServiceResponse<null>>('/refunds', input);
}

/** POST /refunds/:id/approve — 审批通过（REFUND_APPROVE）。 */
export async function approveRefund(id: string, input: ApproveRefundInput = {}): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/refunds/${encodeURIComponent(id)}/approve`,
    input,
  );
}

/** POST /refunds/:id/reject — 拒绝退款（REFUND_APPROVE）。 */
export async function rejectRefund(id: string, input: RejectRefundInput): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/refunds/${encodeURIComponent(id)}/reject`,
    input,
  );
}

/**
 * POST /refunds/:id/execute — 执行退款（REFUND_EXECUTE）。
 * 透传后端结果，含 51303 等错误码。
 */
export async function executeRefund(id: string, input: ExecuteRefundInput = {}): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/refunds/${encodeURIComponent(id)}/execute`,
    input,
  );
}
